
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError, of } from "rxjs";
import { catchError, switchMap, map, shareReplay } from "rxjs/operators";
import { setting } from '../setting';
import { AuthorizatedGuard } from "app/pages/com.usblick.common/guard/authorizated.guard";
import { RestService } from 'app/shared/rest.service';
import { NotificationService } from './app/shared/notifications/notification.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {

    private isRedirecting = false;
    private lastVerification = 0;
    private pendingVerification: Observable<boolean> | null = null;
    private readonly CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutos

    // true solo después de que verifyToken() confirmó una sesión válida al
    // menos una vez en este load de la app. Distingue "nunca hubo sesión"
    // (home sin login, guest antes de buscar — no expulsar) de "había sesión
    // y dejó de ser válida" (expiró de verdad — ahí sí corresponde expulsar).
    private hasConfirmedSession = false;

    // Endpoints que no requieren sesión válida: datos públicos que un guest
    // necesita antes de tener agente asignado (autocomplete, contenido de
    // home) y el propio endpoint que crea la sesión de guest. Sin esto, la
    // verificación dispara en la primera de estas llamadas y expulsa al
    // guest antes de que llegue a buscar.
    // TODO: completar la lista con el resto del inventario de llamadas
    // pre-búsqueda (día 1 del plan B2C) — todavía no está completo.
    private readonly SESSION_EXEMPT_PATTERNS: string[] = [
        '/user/current?from=booking',
        '/guest', // API\GuestController@store — alta de invitado B2C
        '/utilities/searchlocations',
        'getClientsSettingAppBooking',
    ];

    constructor(
        private router: Router,
        private authorizatedGuard: AuthorizatedGuard,
        private notificationService: NotificationService,
        private _restService: RestService
    ) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler): Observable<HttpEvent<any>>
    {
        // La sesión viaja en la cookie HttpOnly auth_token; el browser la adjunta
        // solo si el request pide credentials. Acotado a la API de booking para no
        // romper llamadas a otros orígenes con CORS wildcard (igual que en AuthAPP).
        if (req.url.startsWith(setting.apiBookingUrl)) {
            req = req.clone({ withCredentials: true });
        }

        // Excluir los endpoints que no requieren sesión (ver SESSION_EXEMPT_PATTERNS)
        if (this.isSessionExempt(req.url)) {
            return next.handle(this.addHeaders(req));
        }

        // No interceptor para assets
        if (req.url.includes('.svg') || req.url.includes('.png') || req.url.includes('.jpg')) {
            return next.handle(req);
        }

        const now = Date.now();
        const needsVerification = (now - this.lastVerification) >= this.CHECK_INTERVAL;

        // ✅ Si no necesita verificación (menos de 5 minutos desde la última), continuar directamente
        if (!needsVerification) {
            // console.log('⏭️ Verificación no necesaria (última hace', Math.round((now - this.lastVerification) / 1000), 'segundos)');
            return next.handle(this.addHeaders(req));
        }



        return this.verifyToken().pipe(
            switchMap(isValid => {
                // Se throttlea acá SIEMPRE, sea cual sea el resultado — si no,
                // mientras no hay sesión, cada request de la página vuelve a
                // disparar needsVerification y encadena un verifyToken() por
                // cada una (countries, currencies, locationAgency, etc.),
                // en serie, antes de dejar pasar nada.
                this.lastVerification = Date.now();

                if (isValid) {
                    this.hasConfirmedSession = true;
                    // console.log('✅ Token válido, próxima verificación en 5 minutos');
                    return next.handle(this.addHeaders(req));
                }

                if (this.hasConfirmedSession) {
                    // Hubo sesión válida y dejó de estarlo: expiró de verdad.
                    this.handleInvalidToken();
                    return throwError(() => new Error('Token inválido'));
                }

                // Nunca se confirmó sesión en este load — no es una expiración,
                // es simplemente que todavía no hay sesión (home sin login,
                // guest antes de buscar). No expulsar; las rutas que sí la
                // requieren están protegidas por AuthorizatedGuard.
                return next.handle(this.addHeaders(req));
            })
        );
    }

    private isSessionExempt(url: string): boolean {
        return this.SESSION_EXEMPT_PATTERNS.some(pattern => url.includes(pattern));
    }

    private verifyToken(): Observable<boolean> {
        // Si ya hay una verificación en curso, reutilizarla
        if (this.pendingVerification) {
            return this.pendingVerification;
        }

        this.pendingVerification = this._restService.doGet("app", "user/current?from=booking").pipe(
            map((response: any) => {
                const isValid = response.res === 1 || response?.user?.id;
                return isValid;
            }),
            catchError(() => of(false)),
            map(result => {
                this.pendingVerification = null;
                return result;
            }),
            // doGet() returns a cold Observable — without shareReplay, every concurrent
            // subscriber to this "cached" reference would trigger its own real HTTP call,
            // defeating the dedup this method is meant to provide.
            shareReplay(1)
        );

        return this.pendingVerification;
    }

    private handleInvalidToken(): void {
        if (this.isRedirecting) return;

        this.isRedirecting = true;
        this.notificationService.warning('Sesión expirada, vuelva a loguearse');

        setTimeout(() => {
            this.authorizatedGuard.letsRedirect();
            this.isRedirecting = false;
        }, 2000);
    }

    // Orígenes propios a los que sí corresponde mandar las credenciales de
    // cliente/API. Cualquier otro destino (nominatim, servicios locales,
    // terceros) recibe solo Content-Type/Accept — antes recibía todo.
    private isOwnApi(url: string): boolean {
        return [
            setting.apiBookingUrl,
            setting.authApiUrl,
        ]
            .filter(base => !!base)
            .some(base => url.startsWith(base.trim()));
    }

    // Replica lo que el interceptor anterior mandaba en la práctica: cada
    // clone partía de req.headers y pisaba al anterior, así que Content-Type,
    // client-id, client-secret y ApiUser nunca llegaban. No se agrega
    // Content-Type a propósito (rompería subidas con FormData).
    // Ya no se arma Authorization a mano: la sesión viaja en la cookie HttpOnly, vía withCredentials.
    private addHeaders(req: HttpRequest<any>): HttpRequest<any> {
        let headers = req.headers;

        if (!this.isOwnApi(req.url)) {
            if (!headers.has('Accept')) {
                headers = headers.set('Accept', 'application/json');
            }
            return req.clone({ headers });
        }

        if (!headers.has('x-api-user')) {
            headers = headers.set('x-api-user', setting.booking_api_user);
        }
        if (!headers.has('x-api-key')) {
            headers = headers.set('x-api-key', setting.booking_api_key);
        }

        return req.clone({ headers });
    }
}
